import type { AiChange } from '@roll1/shared';
import { dnd5eTemplate } from '@roll1/shared/builtinTemplates';
import type { TranslationKeys } from '../../i18n';

/**
 * The words of one field of a change, for the opened group of the change card (owner, 23.10: forty lines «Лист «Шая»
 * изменён» told nothing — each field must say what it is and what it was / is now). The sheet's field names come
 * from the D&D 5e template (its labels are the Russian names the sheet shows); English has its own short names.
 */

type Translate = (key: keyof TranslationKeys) => string;

const RU_SHEET = new Map<string, string>();
for (const section of dnd5eTemplate.sections) for (const f of section.fields) if (!RU_SHEET.has(f.key)) RU_SHEET.set(f.key, f.label);

const ABIL: Record<string, string> = { str: 'STR', dex: 'DEX', con: 'CON', int: 'INT', wis: 'WIS', cha: 'CHA' };
const EN_SHEET: Record<string, string> = {
  name: 'name', race: 'race', class_name: 'class', level: 'level', background: 'background', alignment: 'alignment', xp: 'XP',
  ac: 'AC', armor_ac: 'armor bonus', shield_on: 'shield', misc_ac: 'other AC bonus', hp: 'HP', max_hp: 'max HP', temp_hp: 'temp HP',
  hit_dice: 'hit dice', hit_dice_used: 'hit dice used', speed: 'speed', initiative_bonus: 'initiative bonus', initiative: 'initiative',
  death_save_success: 'death saves (success)', death_save_fail: 'death saves (fail)', conditions: 'conditions', inspiration: 'inspiration',
  passive_perception_bonus: 'passive perception bonus', passive_perception: 'passive perception', prof_bonus: 'proficiency bonus',
  personality_traits: 'personality traits', ideals: 'ideals', bonds: 'bonds', flaws: 'flaws', notes: 'notes', combat_notes: 'combat notes',
  spellcasting_ability: 'spellcasting ability', spellcasting_mod: 'spellcasting modifier', spell_save_dc: 'spell save DC', spell_attack: 'spell attack',
  cp: 'copper', sp: 'silver', ep: 'electrum', gp: 'gold', pp: 'platinum', tokenDescription: 'token text',
};

const humanize = (key: string): string => key.replace(/_/g, ' ');

const RP_FIELD_KEYS: Record<string, keyof TranslationKeys> = {
  ideal: 'roleplay.labelIdeal', bond: 'roleplay.labelBond', flaw: 'roleplay.labelFlaw',
  backstory: 'roleplay.cardBackstory', manners: 'roleplay.cardManners', goals: 'roleplay.cardGoals', npcs: 'roleplay.cardNpc',
  factions: 'roleplay.cardFactions', sessionLog: 'roleplay.cardSessionLog', moments: 'roleplay.cardMoments', boardConfig: 'roleplay.boardTitle',
};

/** Name of a sheet value key in the reader's language. */
export function sheetFieldName(key: string, locale: string): string {
  if (locale === 'ru') return RU_SHEET.get(key) ?? humanize(key);
  if (EN_SHEET[key] !== undefined) return EN_SHEET[key]!;
  if (ABIL[key] !== undefined) return ABIL[key]!;
  const mod = /^(str|dex|con|int|wis|cha)_mod$/.exec(key);
  if (mod) return `${ABIL[mod[1]!]} modifier`;
  const save = /^(str|dex|con|int|wis|cha)_save_prof$/.exec(key);
  if (save) return `${ABIL[save[1]!]} save`;
  const skill = /^(.+)_(prof|expertise)$/.exec(key);
  if (skill) return `${humanize(skill[1]!)} ${skill[2] === 'prof' ? 'proficiency' : 'expertise'}`;
  return humanize(key);
}

const fill = (template: string, values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [k, v]) => text.split(`{${k}}`).join(v), template);

/** What the field of this change is called. */
export function changeFieldName(ch: AiChange, t: Translate, locale: string): string {
  const field = ch.field ?? '';
  if (field === 'equipped') return fill(t('aiField.equipped'), { item: ch.label ?? '' });
  const own = `aiField.${field}` as keyof TranslationKeys;
  if (t(own) !== own) return t(own);
  if (ch.entity.kind === 'journal' && (['description', 'content', 'gmNotes', 'rewards'] as const).includes(field as 'description')) {
    return t(`aiCard.field.${field}` as keyof TranslationKeys);
  }
  // A key of the «Отыгрыш» board (journalled one line per key: roleplay_json.backstory…) — named as the board names it.
  if (ch.entity.kind === 'sheet' && field.startsWith('roleplay_json.')) {
    const rp = RP_FIELD_KEYS[field.slice('roleplay_json.'.length)];
    return rp !== undefined ? t(rp) : humanize(field.slice('roleplay_json.'.length));
  }
  if (ch.entity.kind === 'sheet') return sheetFieldName(field, locale);
  return humanize(field);
}

const US = String.fromCharCode(31);
const MAX = 140;

/** A value of a field, shown («—» for nothing, yes / no, a list joined); ids of beats are named by `beatName`. */
export function changeValue(v: unknown, t: Translate, beatName?: (id: string) => string | undefined): string {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'boolean') return v ? t('aiGroup.yes') : t('aiGroup.no');
  if (Array.isArray(v)) {
    if (v.length === 0) return '—';
    const items = v.map((x) => (typeof x === 'string' ? (beatName?.(x) ?? x.split(US).join(': ')) : typeof x === 'object' && x !== null ? String((x as { text?: unknown; name?: unknown }).text ?? (x as { name?: unknown }).name ?? JSON.stringify(x)) : String(x)));
    const text = items.join(', ');
    return text.length > MAX ? `${text.slice(0, MAX - 1)}…` : text;
  }
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.x === 'number' && typeof o.y === 'number') return `${Math.round(o.x)}, ${Math.round(o.y)}`;
    if (typeof o.label === 'string') return o.label;
    const text = JSON.stringify(v);
    return text.length > MAX ? `${text.slice(0, MAX - 1)}…` : text;
  }
  const text = String(v);
  return text.length > MAX ? `${text.slice(0, MAX - 1)}…` : text;
}
